import React, { useRef, useState } from 'react';
import { Camera, X, Upload, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { Button } from './ui/button';
import { resolveAvatar } from '../lib/avatar';

/**
 * Avatar picker used on the Profile page.
 *
 * - Renders the current avatar (uploaded photo, emoji, legacy OAuth picture,
 *   or a default silhouette in that priority order).
 * - Tapping "Change photo" opens a lightweight dialog that lets the user
 *   either upload a photo from their device (camera on mobile, file picker
 *   on desktop) or pick a small emoji as a lightweight fallback.
 * - Uploaded images are downscaled client-side to max 512×512 JPEG (quality
 *   0.85) before the base64 upload so we never trip the backend's 5 MB cap.
 * - After a successful save, calls `onUpdated()` so the parent can refresh
 *   the auth context (via `checkAuth`) and the new avatar appears everywhere.
 */
function AvatarPicker({ user, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Resolve what to render as the current avatar.
  const currentAvatar = resolveAvatar(user);

  const openDialog = () => {
    setPreview(null);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setPreview(null);
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reject non-images defensively (the accept attribute helps but Android
    // browsers occasionally allow anything through).
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    // 15 MB raw upper bound — anything bigger is almost certainly a mistake.
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Image is too large (max 15 MB before compression)');
      return;
    }
    try {
      const dataUrl = await downscaleImage(file, 512, 0.85);
      setPreview(dataUrl);
    } catch (err) {
      toast.error('Could not read that image. Try another.');
    }
  };

  const saveAvatar = async (avatarValue) => {
    setBusy(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/update-avatar`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ avatar: avatarValue }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Update failed (HTTP ${res.status})`);
        return;
      }
      toast.success('Avatar updated');
      closeDialog();
      onUpdated?.();
    } catch (e) {
      toast.error('Network error');
    } finally {
      setBusy(false);
    }
  };

  const EMOJI_CHOICES = ['👤', '🙂', '😊', '💪', '🧘', '🌱', '☀️', '⭐'];

  return (
    <>
      {/* Current avatar + change button */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={openDialog}
          className="relative w-16 h-16 rounded-full border-2 border-zinc-200 bg-zinc-100 flex items-center justify-center overflow-hidden hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          data-testid="avatar-open-picker-btn"
          aria-label="Change avatar"
        >
          {currentAvatar.kind === 'image' && (
            <img src={currentAvatar.value} alt="Your avatar" className="w-full h-full object-cover" />
          )}
          {currentAvatar.kind === 'emoji' && (
            <span className="text-3xl leading-none">{currentAvatar.value}</span>
          )}
          {currentAvatar.kind === 'none' && (
            <UserIcon className="w-8 h-8 text-zinc-500" />
          )}
          <span className="absolute bottom-0 right-0 bg-emerald-600 text-white rounded-full p-1 border-2 border-white">
            <Camera className="w-3 h-3" />
          </span>
        </button>
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openDialog}
            data-testid="avatar-change-btn"
          >
            <Camera className="w-4 h-4 mr-1.5" />
            Change photo
          </Button>
          <p className="text-[11px] text-zinc-500 mt-1">Upload from device or pick an emoji</p>
        </div>
      </div>

      {/* Dialog */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
          data-testid="avatar-picker-dialog"
          onClick={closeDialog}
        >
          <div
            className="bg-white w-full max-w-md rounded-xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
              <h3 className="text-sm font-bold text-zinc-900">Choose your avatar</h3>
              <button
                onClick={closeDialog}
                className="p-1 rounded hover:bg-zinc-100"
                data-testid="avatar-picker-close-btn"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-zinc-600" />
              </button>
            </div>

            <div className="p-4 space-y-5">
              {/* Upload from device */}
              <div>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Upload from device</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  onChange={onFileChosen}
                  className="hidden"
                  data-testid="avatar-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  data-testid="avatar-file-select-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Select photo from computer or phone
                </Button>
                {preview && (
                  <div className="mt-3 flex items-center gap-3">
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-20 h-20 rounded-full border-2 border-emerald-400 object-cover"
                      data-testid="avatar-preview"
                    />
                    <div className="flex-1">
                      <p className="text-xs text-zinc-600 mb-2">Looks good?</p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => saveAvatar(preview)}
                          disabled={busy}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="avatar-save-btn"
                        >
                          {busy ? 'Saving…' : 'Use this photo'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => { setPreview(null); fileInputRef.current.value = ''; }}
                          disabled={busy}
                          data-testid="avatar-retake-btn"
                        >
                          Try another
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Emoji quick picks */}
              <div>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Or pick an emoji</p>
                <div className="grid grid-cols-8 gap-2">
                  {EMOJI_CHOICES.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => saveAvatar(`emoji:${e}`)}
                      disabled={busy}
                      className="aspect-square rounded-lg border border-zinc-200 hover:border-emerald-400 hover:bg-emerald-50 text-2xl flex items-center justify-center disabled:opacity-50"
                      data-testid={`avatar-emoji-${e}`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Read a File → downscale to max `maxSide` on longest side → return JPEG
 *  base64 data URL at `quality`. Uses an offscreen canvas — no libraries. */
function downscaleImage(file, maxSide, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('decode failed'));
      img.onload = () => {
        try {
          let { width, height } = img;
          if (Math.max(width, height) > maxSide) {
            const scale = maxSide / Math.max(width, height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (err) {
          reject(err);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default AvatarPicker;
